import React from 'react';
import { Box, Card, CardContent, Typography, useTheme, Stack, Button, LinearProgress, IconButton } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import {
  OpenInNew as OpenInNewIcon,
  Assessment as AssessmentIcon,
  Visibility as VisibilityIcon,
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  Psychology as PsychologyIcon,
  Public as PublicIcon,
  Search as SearchIcon,
  Photo as PhotoIcon
} from '@mui/icons-material';

const ResearchToolBox = ({
  toolCall,
  artifacts = [],
  onOpenReport,
  loading = false,
  liveStartTs,
  placeCompletedAtBottom = false,
  readOnly = false
}) => {
  const theme = useTheme();
  const [progress, setProgress] = React.useState(0);
  const [etaText, setEtaText] = React.useState('');

  // Per-tool default durations (ms)
  const getDefaultDurationMs = (toolName) => {
    if (toolName === 'landing_page_analysis') return 2 * 60 * 1000; // 2 minutes
    return 5 * 60 * 1000; // default 5 minutes
  };

  // Drive animated progress with countdown until completion; hold at 99%
  React.useEffect(() => {
    if (!loading) {
      setProgress(0);
      setEtaText('');
      return;
    }

    const toolName = toolCall?.tool || 'unknown_tool';
    const startedAt = typeof liveStartTs === 'number' ? liveStartTs : Date.now();
    const duration = getDefaultDurationMs(toolName);

    const tick = () => {
      const now = Date.now();
      const elapsed = Math.max(0, now - startedAt);
      const frac = Math.min(0.99, elapsed / duration); // clamp at 99%
      const pct = Math.max(1, Math.floor(frac * 100));
      setProgress(pct);
      const remainingMs = Math.max(0, Math.round(duration - elapsed));
      const mins = Math.floor(remainingMs / 60000);
      const secs = Math.floor((remainingMs % 60000) / 1000);
      setEtaText(mins > 0 ? `${mins}m ${secs}s left` : `${secs}s left`);
    };

    tick();
    const id = window.setInterval(tick, 500);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, liveStartTs, toolCall?.tool]);

  // Map tool names to icons and colors
  const getToolIcon = (toolName) => {
    const iconMap = {
      landing_page_analysis: <AssessmentIcon />,
      reviews_analysis: <AssessmentIcon />,
      generate_marketing_image: <PhotoIcon />,
      market_analysis: <AnalyticsIcon />,
      trend_analysis: <TrendingUpIcon />,
      competitor_analysis: <BusinessIcon />,
      user_research: <PsychologyIcon />,
      website_analysis: <PublicIcon />,
      search_analysis: <SearchIcon />
    };
    return iconMap[toolName] || <AssessmentIcon />;
  };

  const getToolColor = (toolName) => {
    const colorMap = {
      landing_page_analysis: theme.palette.primary.main,
      reviews_analysis: theme.palette.info.main,
      generate_marketing_image: theme.palette.secondary.main,
      market_analysis: theme.palette.secondary.main,
      trend_analysis: theme.palette.success.main,
      competitor_analysis: theme.palette.warning.main,
      user_research: theme.palette.info.main,
      website_analysis: theme.palette.error.main,
      search_analysis: theme.palette.grey[600]
    };
    return colorMap[toolName] || theme.palette.primary.main;
  };

  const getToolDisplayName = (toolName) => {
    const nameMap = {
      landing_page_analysis: 'Landing Page Analysis',
      reviews_analysis: 'Reviews Analysis',
      generate_marketing_image: 'Generate Marketing Image',
      market_analysis: 'Market Analysis',
      trend_analysis: 'Trend Analysis',
      competitor_analysis: 'Competitor Analysis',
      user_research: 'User Research',
      website_analysis: 'Website Analysis',
      search_analysis: 'Search Analysis'
    };
    return nameMap[toolName] || toolName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const handleOpenReport = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (artifacts && artifacts.length > 0) {
      const artifact = artifacts[0];
      if (artifact.url) {
        // Open the report URL in a new tab while keeping chat open
        window.open(artifact.url, '_blank');
      }
    } else if (parsedTr?.url) {
      // If no artifacts but we have tool_result with URL, open that
      window.open(parsedTr.url, '_blank');
    }
  };

  const handleNavigateToReport = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();

    console.log('ResearchToolBox handleNavigateToReport clicked');
    console.log('artifacts:', artifacts);
    console.log('toolCall:', toolCall);
    console.log('toolCall.tool_result:', toolCall?.tool_result);
    console.log('onOpenReport:', onOpenReport);

    // Check if we have artifacts or tool_result data
    if ((artifacts && artifacts.length > 0) || toolCall?.tool_result) {
      const artifact = artifacts && artifacts.length > 0 ? artifacts[0] : null;
      console.log('Calling onOpenReport with:', { toolCall, artifacts, artifact });
      // Just open the report viewer - NO URL changes
      if (onOpenReport) {
        onOpenReport(toolCall, artifacts, artifact);
      } else {
        console.error('onOpenReport is not defined!');
      }
    } else {
      console.log('No artifacts or tool_result data available');
    }

    return false;
  };

  const toolName = toolCall?.tool || 'unknown_tool';
  const toolColor = getToolColor(toolName);
  const toolIcon = getToolIcon(toolName);
  const displayName = getToolDisplayName(toolName);

  const tr = toolCall?.tool_result;

  // Helper to check if a string is valid JSON that can be parsed
  const isValidJsonString = (str, toolName) => {
    if (typeof str !== 'string') return false;
    try {
      // Remove markdown code fences if present
      let jsonString = str.trim();
      if (jsonString.startsWith('```')) {
        jsonString = jsonString.replace(/^```(?:json)?\s*\n?/, '').replace(/\n?```\s*$/, '');
      }
      const parsed = JSON.parse(jsonString);

      // For get_who_to_buy_strategy, check if it has the expected structure
      if (toolName === 'get_who_to_buy_strategy' || toolName === 'functions.get_who_to_buy_strategy') {
        return !!(
          parsed &&
          typeof parsed === 'object' &&
          (parsed.get || parsed.who || parsed.to || parsed.by || parsed.client || parsed.category)
        );
      }

      // For other tools, if it parses successfully and is an object, it's likely valid
      return parsed && typeof parsed === 'object';
    } catch (e) {
      return false;
    }
  };

  // Helper to parse tool_result if it's a string
  const parseToolResultIfNeeded = (res, toolName) => {
    if (typeof res === 'string' && isValidJsonString(res, toolName)) {
      try {
        let jsonString = res.trim();
        if (jsonString.startsWith('```')) {
          jsonString = jsonString.replace(/^```(?:json)?\s*\n?/, '').replace(/\n?```\s*$/, '');
        }
        return JSON.parse(jsonString);
      } catch (e) {
        return res;
      }
    }
    return res;
  };

  // Parse tool_result if needed for checking
  const parsedTr = parseToolResultIfNeeded(tr, toolName);

  const hasUsablePayload = (res) => {
    // For get_who_to_buy_strategy, check if it has valid structure (even if string)
    if (toolName === 'get_who_to_buy_strategy' || toolName === 'functions.get_who_to_buy_strategy') {
      if (typeof res === 'string' && isValidJsonString(res, toolName)) {
        return true;
      }
      return !!(res && typeof res === 'object' && (res.get || res.who || res.to || res.by || res.client || res.category));
    }
    // For other tools, check for image/url payloads
    return !!(res && typeof res === 'object' && (res.b64_image || res.url || res.result?.b64_image || res.result?.url));
  };

  const isErrorResult = (res) => {
    // If it's a string, check if it's valid JSON for this tool type
    if (typeof res === 'string') {
      // If it's valid JSON for this tool, it's not an error
      if (isValidJsonString(res, toolName)) {
        return false;
      }
      // Otherwise, treat string as error (could be an error message)
      return true;
    }
    // For objects, check for explicit error fields
    return !!(res && typeof res === 'object' && (res.error || res.result?.error));
  };

  const hasError = isErrorResult(tr);
  const hasUsable = hasUsablePayload(parsedTr);

  return (
    <Card
      sx={{
        mb: 0,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        transition: 'all 0.2s ease-in-out',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: `0 4px 12px ${theme.palette.action.hover}`,
          transform: 'translateY(-1px)'
        }
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{
                p: 1,
                borderRadius: 1,
                backgroundColor: `${toolColor}20`,
                color: toolColor,
                display: 'flex',
                alignItems: 'center',
                mr: 1
              }}
            >
              {toolIcon}
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                {displayName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Research Tool
                </Typography>
                {!loading &&
                  !hasError &&
                  !placeCompletedAtBottom &&
                  ((artifacts && artifacts.length > 0) || (toolCall && toolCall.tool_result)) &&
                  (hasUsable ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: theme.palette.success.main }} />
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        Completed
                      </Typography>
                    </Box>
                  ) : null)}
              </Box>
            </Box>
          </Box>
          {!readOnly && !loading && !hasError && ((artifacts && artifacts.length > 0) || hasUsable) && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={handleNavigateToReport}
                sx={{ height: 28, px: 1.5, fontSize: '0.75rem' }}
              >
                View
              </Button>
              <IconButton size="small" onClick={handleOpenReport} sx={{ width: 28, height: 28 }}>
                <OpenInNewIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>
          )}
        </Box>

        {/* Loading state during streaming */}
        {loading ? (
          <Box>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 1, bgcolor: theme.palette.grey[200] }} />
            <Box sx={{ mt: 0.75, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Generating…
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {progress}%{etaText ? ` • ${etaText}` : ''}
              </Typography>
            </Box>
          </Box>
        ) : /* Completed / error / empty states */
        (artifacts && artifacts.length > 0) || (toolCall && toolCall.tool_result) ? (
          hasError ? (
            <Typography variant="body2" sx={{ color: theme.palette.error.main }}>
              Tool execution failed
            </Typography>
          ) : placeCompletedAtBottom ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: theme.palette.success.main }} />
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Completed
              </Typography>
            </Box>
          ) : toolName === 'generate_marketing_image' && hasUsablePayload(parsedTr) ? (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ position: 'relative', width: 120, height: 120 }}>
                <Box
                  component="img"
                  src={parsedTr.b64_image ? `data:image/png;base64,${parsedTr.b64_image}` : parsedTr.url}
                  alt="generated"
                  onClick={handleNavigateToReport}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    cursor: 'pointer'
                  }}
                />
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    const src = parsedTr.b64_image ? `data:image/png;base64,${parsedTr.b64_image}` : parsedTr.url;
                    if (!src) return;
                    const a = document.createElement('a');
                    a.href = src;
                    const placement = parsedTr.meta?.placement || parsedTr.result?.meta?.placement || 'image';
                    a.download = `${placement}-${Date.now()}.png`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  }}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    bgcolor: 'rgba(0,0,0,0.4)',
                    color: '#fff',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' }
                  }}
                >
                  <DownloadIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Image generated ({parsedTr.size || parsedTr.result?.size || '—'}) for{' '}
                  {parsedTr.meta?.placement || parsedTr.result?.meta?.placement || 'custom placement'}
                </Typography>
                {(parsedTr.meta?.overlay_text || parsedTr.result?.meta?.overlay_text) && (
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, whiteSpace: 'pre-wrap' }}>
                    {parsedTr.meta?.overlay_text || parsedTr.result?.meta?.overlay_text}
                  </Typography>
                )}
              </Box>
            </Box>
          ) : null
        ) : (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
            No reports available yet
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default ResearchToolBox;
